/* nav-demand — demand signals for 0-product model collections.
 *
 * Hard rules learned in review (2026-08-10):
 *  - localStorage and crypto THROW in storage-blocked in-app webviews (the Meta /
 *    TikTok in-app browsers are a large share of our paid traffic). Everything here
 *    is downstream of the device token, so one unguarded read killed every listener
 *    and left a dead vote button. Storage failure must degrade PERSISTENCE, never
 *    FUNCTIONALITY.
 *  - navigator.sendBeacon returns false when it refuses the payload; the event is
 *    then silently lost. Always fall back to the keepalive fetch on false.
 *  - 🔴 The beacon Blob MUST carry a CORS-safelisted content type. Measured against
 *    the live collector 2026-08-10: an `application/json` beacon needs a preflight,
 *    sendBeacon cannot preflight, so the request dies as net::ERR_FAILED — and
 *    sendBeacon STILL returns true, so the return-value check above cannot save it.
 *    The same payload sent as `text/plain` is a simple request and lands. The
 *    collector parses the JSON body irrespective of the declared type.
 *  - 🔴 SUCCESS FLAGS ARE GATED ON THE RESPONSE (2026-08-11). v1 wrote
 *    `nd_voted_*` / `nd_waitlisted_*` unconditionally, immediately after calling
 *    send(). A failed POST — collector down, 403, offline, flaky mobile network —
 *    therefore left the shopper permanently locked into a "Requested ✓" button
 *    that could never be retried, with NOTHING recorded on the server. The
 *    strongest-intent shoppers on the worst connections were exactly the ones
 *    silently dropped, and the lost signal is unrecoverable: they cannot vote
 *    again, and we never knew they tried.
 *
 *    So: the optimistic UI text stays instant (the shopper must see an immediate
 *    acknowledgement), but the PERSISTENT flag is written only in the fetch
 *    `.then` on `response.ok`, or when sendBeacon confirms it queued the payload.
 *    On failure the control reverts to enabled with its original label, so a
 *    retry stays possible. Persistence follows the server; the UI follows the
 *    shopper.
 */
(function () {
  var root = document.getElementById('nav-demand');
  if (!root) return;
  if (root.dataset && root.dataset.ndInit) return;   // section re-render / double include
  if (root.dataset) root.dataset.ndInit = '1';

  var EP = 'https://track.ordernation.com/api/demand/event';
  var handle = root.getAttribute('data-model-handle');

  // ---------------------------------------------------------------- storage
  var mem = {};
  function lsGet(k) {
    try { return localStorage.getItem(k); }
    catch (e) { return mem[k] === undefined ? null : mem[k]; }
  }
  function lsSet(k, v) {
    try { localStorage.setItem(k, v); }
    catch (e) { mem[k] = String(v); }
  }
  function newToken() {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return String(Math.random()).slice(2) + String(Date.now());
  }
  var tok = lsGet('nd_tok');
  if (!tok) { tok = newToken(); lsSet('nd_tok', tok); }

  // --------------------------------------------------------------- transport
  // `onDone(ok)` is invoked EXACTLY once per send, with ok === true only when the
  // server actually took the event (2xx) or sendBeacon confirmed it queued it.
  function post(json, onDone) {
    try {
      fetch(EP, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: json, keepalive: true })
        .then(function (r) { onDone(!!(r && r.ok)); },
              function () { onDone(false); });
    } catch (e) { onDone(false); }
  }
  function send(event, extra, useBeacon, onDone) {
    var done = onDone || function () {};
    var called = false;
    function once(ok) { if (!called) { called = true; done(ok); } }
    var body = Object.assign({ event: event, model_handle: handle, state: 'demand',
                               device_token: tok }, extra || {});
    var json = JSON.stringify(body);
    if (useBeacon && navigator.sendBeacon) {
      var queued = false;
      try { queued = navigator.sendBeacon(EP, new Blob([json], { type: 'text/plain' })); }
      catch (e) { queued = false; }
      // A queued beacon is the strongest confirmation available — the browser
      // has taken ownership of delivery and there is no response to read.
      if (queued) { once(true); return; }
    }
    post(json, once);
  }

  // -------------------------------------------------------------- vote + waitlist
  var voteBtn = root.querySelector('[data-nd="vote"]');
  var alertForm = root.querySelector('[data-nd="alert"]');

  if (voteBtn && alertForm) {
    var thanksEl = alertForm.querySelector('[data-nd="thanks"]');
    var submitBtn = alertForm.querySelector('button[type=submit]');

    var voteLabel = voteBtn.textContent;
    var submitLabel = submitBtn ? submitBtn.textContent : '';

    function markVoted() {
      voteBtn.disabled = true;
      voteBtn.textContent = 'Requested ✓';
      alertForm.hidden = false;
    }
    function unmarkVoted() {
      voteBtn.disabled = false;
      voteBtn.textContent = voteLabel;
      // The waitlist form stays REVEALED on purpose. Hiding a form the shopper
      // may already be typing into is user-hostile, and the waitlist is the
      // stronger signal anyway — a failed vote should not take it away.
    }
    function markWaitlisted() {
      if (thanksEl) thanksEl.hidden = false;
      if (submitBtn) submitBtn.disabled = true;
    }
    function unmarkWaitlisted() {
      if (thanksEl) thanksEl.hidden = true;
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
    }

    voteBtn.addEventListener('click', function () {
      if (voteBtn.disabled) return;
      markVoted();                               // optimistic, instant
      send('demand_vote', { placement: 'hero' }, false, function (ok) {
        if (ok) lsSet('nd_voted_' + handle, '1'); // persist ONLY on success
        else unmarkVoted();                       // keep the retry possible
      });
    });

    alertForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitBtn && submitBtn.disabled) return;   // no replay via the Enter key
      var v = alertForm.contact.value.trim();
      if (!v) return;
      var channel = v.indexOf('@') > -1 ? 'email' : 'wa';
      markWaitlisted();                          // optimistic, instant
      send('waitlist_submit', { placement: 'hero', contact: { channel: channel, value: v },
                                pref: alertForm.pref.value || null }, false,
           function (ok) {
             if (ok) lsSet('nd_waitlisted_' + handle, '1');
             else unmarkWaitlisted();
           });
    });

    if (lsGet('nd_voted_' + handle)) markVoted();
    if (lsGet('nd_waitlisted_' + handle)) markWaitlisted();
  }

  // ------------------------------------------------------------- navigational
  var waLink = root.querySelector('[data-nd="wa"]');
  if (waLink) {
    waLink.addEventListener('click', function () {
      send('whatsapp_intent_click', { placement: 'hero' }, true);
    });
  }
  Array.prototype.forEach.call(root.querySelectorAll('[data-nd="similar"]'), function (a) {
    a.addEventListener('click', function () {
      send('similar_model_click', { placement: 'similar' }, true);
    });
  });
})();
